/**
 * PostHog product analytics for AIFLedger.
 *
 * IMPORTANT: Emergent hosting injects its own PostHog SDK on `window.posthog`
 * (US region) into every preview + production build. Because posthog-js v1.400
 * no longer supports true multi-instance isolation via the `name` init-arg on
 * an already-initialised singleton, our SDK-based init will only take effect
 * when Emergent's PostHog is NOT already present (e.g. on a custom deployment).
 *
 * As a pragmatic fallback, we ALSO ship a direct-HTTP event capture path
 * (`captureViaHttp`) that always reaches our own PostHog project regardless of
 * `window.posthog` state. `trackEvent()` uses this path.
 *
 * The SDK path is retained so that when Emergent supports disabling their
 * PostHog (or when we move hosts) we automatically get autocapture + pageviews
 * + person profiles at zero code change.
 */
import { getConsent } from "./cookieConsent";

let ph = null;
let loading = null;

async function loadPostHog() {
  const key = process.env.REACT_APP_POSTHOG_KEY;
  if (!key) return null;
  const consent = getConsent();
  if (!consent || !consent.analytics) return null;
  if (ph) return ph;
  if (loading) return loading;

  loading = (async () => {
    try {
      const mod = await import("posthog-js");
      const posthog = mod.default || mod;
      const host = process.env.REACT_APP_POSTHOG_HOST || "https://eu.i.posthog.com";
      // If Emergent already grabbed window.posthog, our SDK init will be a no-op.
      // We still call it so that on non-Emergent hosts everything just works.
      posthog.init(key, {
        api_host: host,
        person_profiles: "identified_only",
        capture_pageview: true,
        capture_pageleave: true,
        disable_session_recording: true,
        autocapture: { dom_event_allowlist: ["click", "submit", "change"] },
      });
      ph = posthog;
      return ph;
    } catch (e) {
      console.warn("[posthog] SDK init failed (will fall back to HTTP capture)", e);
      return null;
    } finally {
      loading = null;
    }
  })();
  return loading;
}

/**
 * Direct HTTP capture — always hits OUR PostHog project regardless of what
 * `window.posthog` is doing. Fire-and-forget.
 */
function captureViaHttp(eventName, props, distinctId) {
  const key = process.env.REACT_APP_POSTHOG_KEY;
  if (!key) return;
  const consent = getConsent();
  if (!consent || !consent.analytics) return;
  const host = process.env.REACT_APP_POSTHOG_HOST || "https://eu.i.posthog.com";
  try {
    const payload = {
      api_key: key,
      event: eventName,
      distinct_id: distinctId || getOrCreateAnonId(),
      properties: {
        ...props,
        $current_url: window.location.href,
        $host: window.location.hostname,
        $pathname: window.location.pathname,
        $lib: "aifledger-http",
      },
      timestamp: new Date().toISOString(),
    };
    // Use sendBeacon when available (survives page navigation), else fetch.
    const url = `${host}/i/v0/e/`;
    if (navigator.sendBeacon) {
      navigator.sendBeacon(url, new Blob([JSON.stringify(payload)],
                                          { type: "application/json" }));
    } else {
      fetch(url, {
        method: "POST",
        keepalive: true,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      }).catch(() => { /* ignore */ });
    }
  } catch { /* ignore */ }
}

const ANON_KEY = "aif_ph_anon_id";
function getOrCreateAnonId() {
  try {
    let id = localStorage.getItem(ANON_KEY);
    if (!id) {
      id = "anon_" + Math.random().toString(36).slice(2) + Date.now().toString(36);
      localStorage.setItem(ANON_KEY, id);
    }
    return id;
  } catch {
    return "anon_fallback";
  }
}

let identifiedUserId = null;

export function initPostHog() { loadPostHog(); }

export function identifyPostHog(user) {
  if (!user?.id) return;
  identifiedUserId = user.id;
  // Try SDK identify (works on non-Emergent hosts)
  loadPostHog().then((p) => {
    if (p) p.identify(user.id, {
      email: user.email, name: user.name,
      active_role: user.active_role, roles: user.roles,
    });
  });
  // Also fire via HTTP so our project always gets the identify event.
  captureViaHttp("$identify", { email: user.email, name: user.name,
                                 active_role: user.active_role }, user.id);
}

export function resetPostHog() {
  identifiedUserId = null;
  try { if (ph) ph.reset(); } catch { /* ignore */ }
}

export function trackEvent(name, props = {}) {
  captureViaHttp(name, props, identifiedUserId);
  // Best-effort SDK path too (harmless if it no-ops).
  loadPostHog().then((p) => { try { if (p) p.capture(name, props); } catch { /* ignore */ } });
}

export function bindPostHogConsent() {
  if (typeof window === "undefined") return;
  window.addEventListener("aif-consent-changed", (e) => {
    const c = e.detail;
    if (c.analytics) initPostHog();
  });
}
